import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Portal,
  Stack,
  Typography,
} from '@mui/material';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterTrayMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/FilterTrayMAIQ';
import { alpha, useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import DataGridMAIQ from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ';
import LoadingIndicator from 'app/components/LoadingIndicator';
import DealerRequiredPageShell from 'app/components/ReusableUIComponents/HighLevelComponents/DealerRequiredPageShell';
import { useTestNurturingPage } from 'app/pages/TestNurturingPage/hooks/useTestNurturingPage';
import { useTestNurturingAutomationModal } from 'app/pages/TestNurturingPage/hooks/useTestNurturingAutomationModal';
import { useTestNurturingJourneysGrid } from 'app/pages/TestNurturingPage/hooks/useTestNurturingJourneysGrid';
import { buildTestNurturingPath, shouldOpenTestNurturingAutomationFromSearch } from 'app/pages/TestNurturingPage/utils/testNurturingRouteUtils';
import { NotAuthorizedPage } from 'app/components/NotAuthorizedPage/Loadable';
import { EmailsGridPage } from 'app/pages/EmailsGridPage';
import TestNurturingAutomationModal from 'app/pages/TestNurturingPage/components/TestNurturingAutomationModal';
import TestTemplateGenerationModal from 'app/pages/TestNurturingPage/components/TestTemplateGenerationModal';
import { EmailGenerationProgressCard } from 'app/NewAIAvatarLayout/features/MiaChat/components/EmailGenerationProgressCard';
import CellChipStatus from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ/components/cells/CellChipStatus';
import {
  selectGenerateInProgress,
  selectGenerateProgress,
  selectGenerateStartedDealerId,
} from 'app/pages/EmailsGridPage/selectors';

type AutomationEventMessage = {
  job_id?: string;
  jobId?: string;
  dealer_id?: string;
  status?: string;
  phase?: string;
  record_type?: string;
  nurturing_stage?: string;
  total_leads?: number;
  completed_leads?: number;
  lead_count?: number;
  results?: Array<{
    lead_id?: string;
    last_status?: string;
    steps_executed?: number;
    error?: string;
    task_id?: string;
    opportunity_id?: string;
  }>;
  [key: string]: unknown;
};

function getAutomationEventMessage(data: unknown): AutomationEventMessage {
  if (!data || typeof data !== 'object') return {};
  const root = data as { message?: unknown };
  if (root.message && typeof root.message === 'object') {
    return root.message as AutomationEventMessage;
  }
  return data as AutomationEventMessage;
}

function toUiEventLabel(eventName: string): string {
  return eventName
    .replace(/^nurturing_test_automation_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

const tabPaperSx = (theme: Theme, selected: boolean) => ({
  p: 2.5,
  borderRadius: 2,
  cursor: 'pointer',
  border: selected
    ? `2px solid ${theme.palette.warning.main}`
    : `1px solid ${theme.palette.divider}`,
  bgcolor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.grey[900], 0.6)
      : theme.palette.grey[50],
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    boxShadow:
      theme.palette.mode === 'dark'
        ? `0 2px 8px ${alpha(theme.palette.common.black, 0.35)}`
        : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
  },
});

export default function TestNurturingComponent() {
  const theme = useTheme();
  const location = useLocation();
  const [journeysGridRefreshKey, setJourneysGridRefreshKey] = useState(0);
  const [automationDetailsOpen, setAutomationDetailsOpen] = useState(false);
  const hasAutoOpenedCompletionRef = useRef(false);
  const automationTourOpenedRef = useRef(false);
  const generateInProgress = useSelector(selectGenerateInProgress);
  const generateProgress = useSelector(selectGenerateProgress);
  const generateStartedDealerId = useSelector(selectGenerateStartedDealerId);
  const {
    canAccess,
    hasSelectedDealer,
    activeMainTab,
    setActiveMainTab,
    breadcrumbsList,
    selectedDealerId,
    segmentsLoading,
    segmentGroupId,
    leadsGridType,
    journeysFilterTray,
    journeysFilterTrayOpen,
    journeysFilterTrayAnchorEl,
    openJourneysFilterTray,
    closeJourneysFilterTray,
    journeysMetadataFiltersKey,
    testTemplateGenerationModalOpen,
    closeTestTemplateGenerationModal,
    openTestTemplateGenerationModal,
    testTemplateGenerationStep,
    goToTemplateGenerationStep2,
    goBackToTemplateGenerationStep1,
    triggerTemplateGenerationFromModal,
    segmentInventories,
    preferredVinsSnapshot,
  } = useTestNurturingPage();

  const { handleJourneysCellClick } = useTestNurturingJourneysGrid(selectedDealerId);

  const refreshJourneysGrid = useCallback(() => {
    setJourneysGridRefreshKey(prev => prev + 1);
  }, []);

  const onAutomationTestSuccess = useCallback(() => {
    refreshJourneysGrid();
    setActiveMainTab(prev => (prev === 'journeys' ? prev : 'journeys'));
  }, [refreshJourneysGrid, setActiveMainTab]);

  const automation = useTestNurturingAutomationModal(
    selectedDealerId,
    Boolean(canAccess),
    { onTestSuccess: onAutomationTestSuccess },
  );

  useEffect(() => {
    automationTourOpenedRef.current = false;
  }, [location.search, selectedDealerId]);

  useEffect(() => {
    if (
      !canAccess ||
      !hasSelectedDealer ||
      !shouldOpenTestNurturingAutomationFromSearch(location.search)
    ) {
      return;
    }
    if (automationTourOpenedRef.current) {
      return;
    }
    automationTourOpenedRef.current = true;
    automation.openTestAutomationModal();
  }, [
    automation.openTestAutomationModal,
    canAccess,
    hasSelectedDealer,
    location.search,
  ]);

  if (!canAccess) {
    return <NotAuthorizedPage />;
  }

  const showJourneysGrid = activeMainTab === 'journeys';
  const showTemplateProgressProxyCard = useMemo(
    () =>
      activeMainTab !== 'templates' &&
      Boolean(generateInProgress) &&
      Boolean(generateStartedDealerId) &&
      String(generateStartedDealerId) === selectedDealerId,
    [activeMainTab, generateInProgress, generateStartedDealerId, selectedDealerId],
  );

  const templateProgressPercentage = useMemo(() => {
    const total = Math.max(generateProgress.total ?? 1, 1);
    const current = Math.min(Math.max(generateProgress.current ?? 0, 0), total);
    return Math.min(100, Math.max(0, Math.round((current / total) * 100)));
  }, [generateProgress.current, generateProgress.total]);

  const templateStreamingDataForCard = useMemo(() => {
    const totalSections = Math.max(generateProgress.total ?? 1, 1);
    return {
      message: {
        sections: Array.from({ length: totalSections }, (_, i) => ({
          index: i + 1,
          main: `Template ${i + 1}`,
          sub_text: '',
        })),
      },
    };
  }, [generateProgress.total]);

  const templateCurrentSectionForCard = useMemo(
    () => ({
      index: Math.min(
        Math.max(1, (generateProgress.current ?? 0) || 1),
        Math.max(generateProgress.total ?? 1, 1),
      ),
      type: 'step' as const,
      main: 'Generating templates...',
      sub_text: `${Math.max(generateProgress.current ?? 0, 0)}/${Math.max(
        generateProgress.total ?? 1,
        1,
      )} templates`,
    }),
    [generateProgress.current, generateProgress.total],
  );

  const isTemplatesProgressCardVisible = useMemo(
    () =>
      showTemplateProgressProxyCard ||
      (activeMainTab === 'templates' &&
        Boolean(generateInProgress) &&
        Boolean(generateStartedDealerId) &&
        String(generateStartedDealerId) === selectedDealerId),
    [
      activeMainTab,
      generateInProgress,
      generateStartedDealerId,
      selectedDealerId,
      showTemplateProgressProxyCard,
    ],
  );
  const automationProgressCardRightOffset = isTemplatesProgressCardVisible
    ? 560
    : 24;
  const automationCompletedJobId = useMemo(() => {
    const payload = automation.automationCompletionPayload;
    if (!payload || typeof payload !== 'object') return '';
    const root = payload as { message?: { job_id?: unknown; jobId?: unknown } };
    const value = root.message?.job_id ?? root.message?.jobId;
    if (value == null) return '';
    return String(value);
  }, [automation.automationCompletionPayload]);

  const latestAutomationSocketMessage = useMemo(
    () =>
      automation.automationSocketMessages.length > 0
        ? automation.automationSocketMessages[
            automation.automationSocketMessages.length - 1
          ]
        : null,
    [automation.automationSocketMessages],
  );

  const automationLiveJobId = useMemo(() => {
    if (latestAutomationSocketMessage) {
      const message = getAutomationEventMessage(latestAutomationSocketMessage.data);
      const liveJobId = message.job_id ?? message.jobId;
      if (liveJobId != null && String(liveJobId).trim()) {
        return String(liveJobId);
      }
    }
    return automationCompletedJobId;
  }, [latestAutomationSocketMessage, automationCompletedJobId]);

  useEffect(() => {
    if (automation.automationInProgress) {
      hasAutoOpenedCompletionRef.current = false;
      return;
    }
    if (!automation.automationCompletionPayload) return;
    if (hasAutoOpenedCompletionRef.current) return;
    setAutomationDetailsOpen(true);
    hasAutoOpenedCompletionRef.current = true;
  }, [
    automation.automationInProgress,
    automation.automationCompletionPayload,
    automationCompletedJobId,
  ]);

  return (
    <DealerRequiredPageShell
      hasDealer={hasSelectedDealer}
      breadcrumbsList={breadcrumbsList}
      pageTitle="Test nurturing journeys and templates before going live"
      contentVariant="centered"
      centeredMaxWidth={1440}
    >
      <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
              mb: 3,
            }}
          >
            <Paper
              elevation={0}
              onClick={automation.openTestAutomationModal}
              sx={{
                p: 2.5,
                display: 'flex',
                gap: 2,
                alignItems: 'flex-start',
                borderRadius: 2,
                border: t => `1px solid ${t.palette.divider}`,
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.grey[900], 0.6)
                    : theme.palette.grey[50],
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow:
                    theme.palette.mode === 'dark'
                      ? `0 4px 12px ${alpha(theme.palette.common.black, 0.35)}`
                      : `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`,
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: 'primary.main',
                  flexShrink: 0,
                }}
              >
                <PlayArrowOutlinedIcon fontSize="medium" />
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Test Nurturing Automation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Run test nurturing flows
                </Typography>
              </Box>
            </Paper>

            <Paper
              elevation={0}
              onClick={openTestTemplateGenerationModal}
              sx={{
                p: 2.5,
                display: 'flex',
                gap: 2,
                alignItems: 'flex-start',
                borderRadius: 2,
                border: t => `1px solid ${t.palette.divider}`,
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? alpha(theme.palette.grey[900], 0.6)
                    : theme.palette.grey[50],
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow:
                    theme.palette.mode === 'dark'
                      ? `0 4px 12px ${alpha(theme.palette.common.black, 0.35)}`
                      : `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`,
                },
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: 'primary.main',
                  flexShrink: 0,
                }}
              >
                <ArticleOutlinedIcon fontSize="medium" />
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Test Template Generation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Generate test templates
                </Typography>
              </Box>
            </Paper>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
              mb: 2,
            }}
          >
            <Paper
              elevation={0}
              onClick={() => setActiveMainTab('journeys')}
              sx={tabPaperSx(theme, activeMainTab === 'journeys')}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Nurturing Journeys
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View active nurturing prospects
              </Typography>
            </Paper>
            <Paper
              elevation={0}
              onClick={() => setActiveMainTab('templates')}
              sx={tabPaperSx(theme, activeMainTab === 'templates')}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Generated Templates
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View generated message templates
              </Typography>
            </Paper>
          </Box>

          {showJourneysGrid && (
            <>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Nurturing Journeys
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <Badge
                  badgeContent={
                    journeysFilterTray.totalActiveCount > 0
                      ? journeysFilterTray.totalActiveCount
                      : undefined
                  }
                  color="primary"
                  sx={{ '& .MuiBadge-badge': { fontSize: 10 } }}
                >
                  <Button
                    size="small"
                    variant="text"
                    color="primary"
                    startIcon={<FilterListIcon />}
                    onClick={e => openJourneysFilterTray(e.currentTarget)}
                    sx={{ textTransform: 'none', minWidth: 0, px: 1 }}
                  >
                    Quick Filters
                  </Button>
                </Badge>
                <FilterTrayMAIQ
                  isLoading={journeysFilterTray.isLoading}
                  open={journeysFilterTrayOpen}
                  onClose={closeJourneysFilterTray}
                  anchorEl={journeysFilterTrayAnchorEl}
                  categories={journeysFilterTray.categories}
                  activeCategoryId={journeysFilterTray.activeCategoryId}
                  onActiveCategoryChange={journeysFilterTray.setActiveCategoryId}
                  optionsForActiveCategory={
                    journeysFilterTray.optionsForActiveCategory
                  }
                  selectedByCategory={journeysFilterTray.selectedByCategory}
                  onSelectionChange={journeysFilterTray.onSelectionChange}
                  totalActiveCount={journeysFilterTray.totalActiveCount}
                  onClearAllFilters={journeysFilterTray.onClearAllFilters}
                  onClearActiveCategory={journeysFilterTray.onClearActiveCategory}
                  onClearAllInPanel={journeysFilterTray.onClearAllInPanel}
                />
              </Box>
            </>
          )}

          <Box sx={{ width: '100%', minHeight: showJourneysGrid ? 420 : 'auto' }}>
            {segmentsLoading && showJourneysGrid ? (
              <LoadingIndicator />
            ) : showJourneysGrid ? (
              leadsGridType ? (
                <DataGridMAIQ
                  key={`journeys-${selectedDealerId}-${segmentGroupId}-${journeysMetadataFiltersKey}-${journeysGridRefreshKey}`}
                  type={leadsGridType}
                  onCellClick={handleJourneysCellClick}
                />
              ) : (
                <Alert severity="info">
                  No segment available for this dealer. Create a segment or pick another
                  dealership.
                </Alert>
              )
            ) : (
              <Box
                data-guidance-tour="test-nurturing-generated-templates"
                sx={{ width: '100%' }}
              >
                <EmailsGridPage
                  embedded
                  hideGenerateEmailTemplateDropdown
                  emailTemplatesGridPreset="testNurturingGenerated"
                  emailTemplateBreadcrumbOrigin={{
                    label: 'Test nurturing',
                    path: buildTestNurturingPath('templates'),
                  }}
                />
              </Box>
            )}
          </Box>

          <TestNurturingAutomationModal
            open={automation.testAutomationModalOpen}
            onClose={automation.closeTestAutomationModal}
            step={automation.testAutomationStep}
            onGoToStep2={automation.goToAutomationStep2}
            onBackToStep1={automation.goToAutomationStep1}
            onGoToStep3={automation.goToAutomationStep3}
            onBackToStep2={automation.goBackToAutomationStep2}
            onFinishAutomation={automation.finishAutomationFlow}
            startTestingLoading={automation.startTestingLoading}
            startTestingError={automation.startTestingError}
            selectedPrimarySegment={automation.testAutomationPrimarySegment}
            primarySegmentOptions={automation.testAutomationPrimarySegmentOptions}
            segmentOptionsLoading={automation.dealerSegmentSettingsLoading}
            segmentOptionsError={automation.dealerSegmentSettingsErrorMessage}
            onPrimarySegmentChange={automation.selectTestAutomationPrimarySegment}
            isStep1NextDisabled={automation.isStep1NextDisabled}
            isStep2NextDisabled={automation.isStep2NextDisabled}
            unsoldRows={automation.unsoldRows}
            unsoldLoading={automation.unsoldLoading}
            unsoldFetching={automation.unsoldFetching}
            unsoldIsFetchingNextPage={automation.unsoldIsFetchingNextPage}
            unsoldErrorMessage={automation.unsoldErrorMessage}
            unsoldTotalDocuments={automation.unsoldTotalDocuments}
            unsoldHasNextPage={automation.unsoldHasNextPage}
            onUnsoldListScroll={automation.onUnsoldListScroll}
            isLeadSelected={automation.isLeadSelected}
            onToggleLeadSelection={automation.toggleLeadSelection}
            unsoldSearchDraft={automation.unsoldSearchDraft}
            unsoldSearchApplied={automation.unsoldSearchApplied}
            onUnsoldSearchDraftChange={automation.setUnsoldSearchDraft}
            onApplyUnsoldSearch={automation.applyUnsoldSearch}
            unsoldQuickFilterOpen={automation.unsoldQuickFilterOpen}
            unsoldQuickFilterAnchorEl={automation.unsoldQuickFilterAnchorEl}
            unsoldQuickFilterActiveCategoryId={
              automation.unsoldQuickFilterActiveCategoryId
            }
            onUnsoldQuickFilterActiveCategoryChange={
              automation.setUnsoldQuickFilterActiveCategoryId
            }
            unsoldQuickFilterCategories={automation.unsoldQuickFilterCategories}
            unsoldQuickFilterOptionsForActiveCategory={
              automation.unsoldQuickFilterOptionsForActiveCategory
            }
            unsoldQuickFilterSelectedByCategory={
              automation.unsoldQuickFilterSelectedByCategory
            }
            onUnsoldQuickFilterSelectionChange={
              automation.onUnsoldQuickFilterSelectionChange
            }
            unsoldQuickFilterTotalActive={automation.unsoldQuickFilterTotalActive}
            onUnsoldQuickFilterClearAll={automation.onUnsoldQuickFilterClearAll}
            onUnsoldQuickFilterClearActiveCategory={
              automation.onUnsoldQuickFilterClearActiveCategory
            }
            onUnsoldQuickFilterClearAllInPanel={
              automation.onUnsoldQuickFilterClearAllInPanel
            }
            onOpenUnsoldQuickFilter={automation.openUnsoldQuickFilter}
            onCloseUnsoldQuickFilter={automation.closeUnsoldQuickFilter}
            onClearUnsoldFiltersAndSearch={
              automation.clearUnsoldQuickFiltersAndSearch
            }
            nurturingStageRows={automation.nurturingStageRows}
            nurturingStagesLoading={automation.nurturingJourneyStagesLoading}
            nurturingStagesError={automation.nurturingJourneyStagesErrorMessage}
            selectedNurturingStageValue={automation.selectedNurturingStageValue}
            onNurturingStageChange={automation.selectNurturingStageValue}
          />

          <TestTemplateGenerationModal
            open={testTemplateGenerationModalOpen}
            onClose={closeTestTemplateGenerationModal}
            step={testTemplateGenerationStep}
            onNext={goToTemplateGenerationStep2}
            onBack={goBackToTemplateGenerationStep1}
            onGenerate={triggerTemplateGenerationFromModal}
            saveLoading={segmentInventories.saveLoading}
            canSavePreferences={segmentInventories.canSavePreferences}
            segmentRows={segmentInventories.segmentRows}
            headerRow={segmentInventories.headerRow}
            loading={segmentInventories.loading}
            error={segmentInventories.error}
            expandedCell={segmentInventories.expandedCell}
            vehiclesForExpandedCell={segmentInventories.vehiclesForExpandedCell}
            vehiclesLoading={segmentInventories.vehiclesLoading}
            vehiclesError={segmentInventories.vehiclesError}
            preferredVinsForExpandedCell={
              segmentInventories.preferredVinsForExpandedCell
            }
            missingVinsForExpandedCell={
              segmentInventories.missingVinsForExpandedCell
            }
            onTogglePreferredVin={segmentInventories.togglePreferredVin}
            onToggleExpanded={segmentInventories.toggleExpanded}
            preferredVinsSnapshot={preferredVinsSnapshot}
            vehiclesBySegmentKind={segmentInventories.vehiclesBySegmentKind}
            prefetchLoading={segmentInventories.prefetchLoading}
          />

          {automation.automationInProgress && (
            <Portal>
              <Box
                sx={{
                  position: 'fixed',
                  bottom: 24,
                  right: automationProgressCardRightOffset,
                  zIndex: 1300,
                  minWidth: 520,
                  transition: 'right 0.2s ease',
                }}
              >
                <EmailGenerationProgressCard
                  isGeneratingEmail={automation.automationInProgress}
                  streamingData={automation.automationStreamingDataForCard}
                  currentSection={automation.automationCurrentSectionForCard}
                  progressOverride={automation.automationProgressPercentage}
                  title="Running Nurturing Test..."
                  onOpenEmailGenerationPopup={() => setAutomationDetailsOpen(true)}
                />
              </Box>
            </Portal>
          )}

          {showTemplateProgressProxyCard && (
            <Portal>
              <Box
                sx={{
                  position: 'fixed',
                  bottom: 24,
                  right: 24,
                  zIndex: 1300,
                  minWidth: 520,
                }}
              >
                <EmailGenerationProgressCard
                  isGeneratingEmail
                  streamingData={templateStreamingDataForCard}
                  currentSection={templateCurrentSectionForCard}
                  progressOverride={templateProgressPercentage}
                  title="Generating Email Templates..."
                  onOpenEmailGenerationPopup={() => setActiveMainTab('templates')}
                />
              </Box>
            </Portal>
          )}

          <Dialog
            open={automationDetailsOpen}
            onClose={() => setAutomationDetailsOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 2,
                maxHeight: '90vh',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              },
            }}
          >
            <DialogTitle
              sx={{
                fontWeight: 700,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              Nurturing test automation progress
            </DialogTitle>
            <DialogContent
              dividers
              sx={{
                bgcolor: 'background.default',
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Job ID: {automationLiveJobId || '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Progress: {automation.automationProgressCurrent}/
                {Math.max(automation.automationProgressTotal, 1)}
              </Typography>
              {automation.automationSocketMessages.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Waiting for automation socket events...
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {(() => {
                    const latestMessage = latestAutomationSocketMessage;
                    if (!latestMessage) return null;
                    const message = getAutomationEventMessage(latestMessage.data);
                    const totalLeads =
                      typeof message.total_leads === 'number'
                        ? message.total_leads
                        : typeof message.lead_count === 'number'
                          ? message.lead_count
                          : null;
                    const completedLeads =
                      typeof message.completed_leads === 'number'
                        ? message.completed_leads
                        : null;
                    const status = message.status
                      ? String(message.status).toUpperCase()
                      : '';
                    const phase = message.phase
                      ? String(message.phase).toUpperCase()
                      : '';
                    const results = Array.isArray(message.results)
                      ? message.results
                      : [];
                    const isCompletedEvent =
                      latestMessage.eventName ===
                      'nurturing_test_automation_completed';

                    return (
                      <Paper
                        key={`${latestMessage.timestamp}-${latestMessage.eventName}`}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderColor: 'divider',
                          bgcolor: 'background.paper',
                        }}
                      >
                        <Stack spacing={1}>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: 1,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Typography variant="body2" fontWeight={700}>
                              {toUiEventLabel(latestMessage.eventName)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(latestMessage.timestamp).toLocaleTimeString()}
                            </Typography>
                          </Box>

                          <Box sx={{ mt: 0.25 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 0.5,
                              }}
                            >
                              <Typography variant="caption" color="text.secondary">
                                Progress
                              </Typography>
                              <Typography
                                variant="caption"
                                color="primary.main"
                                sx={{ fontWeight: 700 }}
                              >
                                {automation.automationProgressPercentage}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={automation.automationProgressPercentage}
                              sx={{
                                height: 7,
                                borderRadius: 999,
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 999,
                                },
                              }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mt: 0.5 }}
                            >
                              {automation.automationProgressCurrent}/
                              {Math.max(automation.automationProgressTotal, 1)} leads processed
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                            {status ? (
                              <CellChipStatus>{status}</CellChipStatus>
                            ) : null}
                            {phase ? (
                              <Chip
                                size="small"
                                label={`Phase: ${phase}`}
                                variant="outlined"
                              />
                            ) : null}
                            {message.nurturing_stage ? (
                              <Chip
                                size="small"
                                label={`Stage: ${String(message.nurturing_stage)}`}
                                variant="outlined"
                              />
                            ) : null}
                            {message.record_type ? (
                              <Chip
                                size="small"
                                label={`Record: ${String(message.record_type)}`}
                                variant="outlined"
                              />
                            ) : null}
                          </Box>

                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {message.job_id || message.jobId ? (
                              <Typography variant="caption" color="text.secondary">
                                Job: {String(message.job_id || message.jobId)}
                              </Typography>
                            ) : null}
                            {message.dealer_id ? (
                              <Typography variant="caption" color="text.secondary">
                                Dealer: {String(message.dealer_id)}
                              </Typography>
                            ) : null}
                            {totalLeads != null ? (
                              <Typography variant="caption" color="text.secondary">
                                Total leads: {totalLeads}
                              </Typography>
                            ) : null}
                            {completedLeads != null ? (
                              <Typography variant="caption" color="text.secondary">
                                Completed leads: {completedLeads}
                              </Typography>
                            ) : null}
                          </Box>

                          {isCompletedEvent && results.length > 0 ? (
                            <>
                              <Divider />
                              <Stack spacing={0.75}>
                                <Typography variant="caption" fontWeight={700}>
                                  Lead Results
                                </Typography>
                                {results.map((result, index) => {
                                  const rowStatus = result.last_status
                                    ? String(result.last_status).toUpperCase()
                                    : result.error
                                      ? 'FAILED'
                                      : 'DONE';
                                  return (
                                    <Paper
                                      key={`${result.lead_id ?? 'lead'}-${index}`}
                                      variant="outlined"
                                      sx={{
                                        p: 1,
                                        borderColor: 'divider',
                                        bgcolor: 'background.paper',
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          gap: 1,
                                          flexWrap: 'wrap',
                                        }}
                                      >
                                        <Typography variant="caption" fontWeight={700}>
                                          Lead {result.lead_id || index + 1}
                                        </Typography>
                                        <CellChipStatus>{rowStatus}</CellChipStatus>
                                      </Box>
                                      {typeof result.steps_executed === 'number' ? (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block', mt: 0.5 }}
                                        >
                                          Steps executed: {result.steps_executed}
                                        </Typography>
                                      ) : null}
                                      {result.error ? (
                                        <Typography
                                          variant="caption"
                                          color="error.main"
                                          sx={{ display: 'block', mt: 0.5 }}
                                        >
                                          {result.error}
                                        </Typography>
                                      ) : null}
                                    </Paper>
                                  );
                                })}
                              </Stack>
                            </>
                          ) : null}
                        </Stack>
                      </Paper>
                    );
                  })()}
                </Box>
              )}
              {!automation.automationInProgress && automation.automationCompletionPayload ? (
                <Box
                  sx={{
                    mt: 2,
                    p: 1.25,
                    borderRadius: 1,
                    bgcolor: 'success.light',
                    color: 'success.dark',
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                  }}
                >
                  <CheckCircleOutline fontSize="small" />
                  <Typography variant="body2" fontWeight={600}>
                    Nurturing test automation completed.
                  </Typography>
                </Box>
              ) : null}
            </DialogContent>
          </Dialog>
      </>
    </DealerRequiredPageShell>
  );
}
